import React, { useMemo, useRef, useState, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  Pill,
  Plus,
  Trash2,
  Save,
  Upload,
  Download,
  ArrowLeft,
  FileSpreadsheet,
  X,
} from "lucide-react";

import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

import {
  getDrugLibrary,
  readCustomDrugs,
  saveCustomDrug,
  deleteCustomDrug,
  importCustomDrugs,
  calcDose,
} from "../data/prescriptions";

const ROUTE_OPTIONS = ["PO", "IV", "SC", "IM", "OTM", "Topical", "OU", "OD", "OS", "IV CRI"];
const FREQ_OPTIONS = ["SID", "BID", "TID", "QID", "EOD", "Weekly", "Monthly", "PRN", "Once"];

const EXPORT_HEADERS = [
  "drug",
  "dose_mg_per_kg",
  "dose_label",
  "route",
  "frequency",
  "duration",
  "notes",
];

const TEMPLATE_ROWS = [
  {
    drug: "Amoxicillin-Clavulanate",
    dose_mg_per_kg: 12.5,
    dose_label: "12.5 mg/kg",
    route: "PO",
    frequency: "BID",
    duration: "10 days",
    notes: "Give with food",
  },
  {
    drug: "Meloxicam",
    dose_mg_per_kg: 0.1,
    dose_label: "0.1 mg/kg",
    route: "PO",
    frequency: "SID",
    duration: "5 days",
    notes: "Baseline renal function first",
  },
];

/**
 * Standalone drug formulary editor.
 * - Left: full drug list (disease-derived + custom) with source badge
 * - Right: add / edit / import / export tools
 */
export default function DrugLibraryEditor({ onBack }) {
  const [filter, setFilter] = useState("");
  const [tick, setTick] = useState(0); // force re-read after mutations
  const [draft, setDraft] = useState(blankDraft());
  const [editingKey, setEditingKey] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    // no-op; kept for future subscriptions
  }, []);

  const library = useMemo(() => getDrugLibrary(), [tick]);
  const customDrugs = useMemo(() => readCustomDrugs(), [tick]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return library;
    return library.filter(
      (d) =>
        d.drug.toLowerCase().includes(q) ||
        (d.route || "").toLowerCase().includes(q) ||
        (d.frequency || "").toLowerCase().includes(q) ||
        (d.notes || "").toLowerCase().includes(q)
    );
  }, [library, filter]);

  const startEdit = (drug) => {
    setEditingKey(drug.drug.toLowerCase());
    setDraft({ ...drug });
  };

  const clearDraft = () => {
    setEditingKey(null);
    setDraft(blankDraft());
  };

  const handleSave = () => {
    if (!draft.drug.trim()) return toast.error("Drug name is required.");
    saveCustomDrug(draft);
    setTick((t) => t + 1);
    toast.success(editingKey ? "Drug updated" : "Drug added");
    clearDraft();
  };

  const handleDelete = (drug) => {
    deleteCustomDrug(drug);
    setTick((t) => t + 1);
    toast.success("Custom drug removed");
  };

  const handleExport = (format = "xlsx") => {
    // Export ALL currently visible drugs (custom + disease-derived) or just customs
    const rows = customDrugs.length > 0 ? customDrugs : library;
    if (rows.length === 0) return toast.error("Nothing to export yet.");
    const data = rows.map((r) => ({
      drug: r.drug,
      dose_mg_per_kg: r.doseMgPerKg ?? "",
      dose_label: r.doseText || "",
      route: r.route || "",
      frequency: r.frequency || "",
      duration: r.duration || "",
      notes: r.notes || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data, { header: EXPORT_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Petsmate-Drugs");
    const fname = `Petsmate-Drugs-${Date.now()}.${format}`;
    if (format === "csv") {
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, fname);
    } else {
      XLSX.writeFile(wb, fname);
    }
    toast.success(`Exported ${rows.length} drug${rows.length === 1 ? "" : "s"}`);
  };

  const handleTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(TEMPLATE_ROWS, { header: EXPORT_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Petsmate-Drug-Template.xlsx");
    toast.success("Template downloaded — fill and re-upload.");
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const first = wb.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[first], { defval: "" });
      if (!rows.length) return toast.error("Sheet is empty.");
      const { added, updated, skipped } = importCustomDrugs(rows);
      setTick((t) => t + 1);
      toast.success(`Imported: ${added} added, ${updated} updated${skipped ? `, ${skipped} skipped` : ""}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not read that file. Use .xlsx or .csv with headers matching the template.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      data-testid="drug-library-editor"
      className="min-h-screen bg-[#F9F8F6] text-[#1A1F1D]"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      {/* Header */}
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-rx-from-drugs"
              variant="ghost"
              onClick={onBack}
              className="rounded-md hover:bg-[#0F4C3A]/5 hover:text-[#0F4C3A]"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Rx
            </Button>
            <Separator orientation="vertical" className="h-6 bg-[#E2E8F0]" />
            <div className="flex items-center gap-2">
              <Pill className="w-5 h-5 text-[#0F4C3A]" />
              <div
                className="text-lg tracking-tight"
                style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 700 }}
              >
                Drug Formulary
              </div>
              <Badge variant="outline" className="border-[#E2E8F0] text-[10px] font-normal ml-1">
                {library.length} drugs
              </Badge>
              {customDrugs.length > 0 && (
                <Badge className="bg-[#0F4C3A] text-white text-[10px] font-normal">
                  {customDrugs.length} custom
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="download-template"
              variant="outline"
              onClick={handleTemplate}
              className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel template
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              data-testid="upload-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button
              data-testid="upload-btn"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              className="rounded-md border-[#0F4C3A]/40 text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Upload Excel / CSV
            </Button>
            <Button
              data-testid="export-xlsx"
              onClick={() => handleExport("xlsx")}
              className="rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white"
            >
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Add/Edit panel */}
        <aside className="lg:col-span-4 order-2 lg:order-1">
          <Card className="border-[#E2E8F0] rounded-xl sticky top-24">
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B66] font-semibold">
                  {editingKey ? "Edit drug" : "Add drug"}
                </div>
                <div
                  className="text-xl tracking-tight mt-1"
                  style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
                >
                  {editingKey ? draft.drug || "Untitled" : "New entry"}
                </div>
                <p className="text-[12px] text-[#5C6B66] mt-1">
                  Stored on this device. Appears instantly in the Rx generator&apos;s drug library popover.
                </p>
              </div>

              <div>
                <MicroLabel>Drug name *</MicroLabel>
                <Input
                  data-testid="drug-name"
                  value={draft.drug}
                  onChange={(e) => setDraft({ ...draft, drug: e.target.value })}
                  placeholder="e.g. Cefovecin (Convenia)"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <MicroLabel>Dose (mg/kg)</MicroLabel>
                  <Input
                    data-testid="drug-mgkg"
                    type="number"
                    step="0.01"
                    value={draft.doseMgPerKg ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === "" ? null : Number(v);
                      setDraft({
                        ...draft,
                        doseMgPerKg: num,
                        doseText: num != null ? `${num} mg/kg` : draft.doseText,
                      });
                    }}
                    placeholder="e.g. 8"
                    className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                  />
                  {draft.doseMgPerKg != null && (
                    <div className="text-[10px] mt-1 text-[#0F4C3A] font-medium">
                      ≈ {calcDose(draft.doseMgPerKg, 10)} per 10 kg
                    </div>
                  )}
                </div>
                <div>
                  <MicroLabel>Dose label</MicroLabel>
                  <Input
                    data-testid="drug-dosetext"
                    value={draft.doseText}
                    onChange={(e) => setDraft({ ...draft, doseText: e.target.value })}
                    placeholder="e.g. 8 mg/kg"
                    className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <MicroLabel>Route</MicroLabel>
                  <Select
                    value={draft.route}
                    onValueChange={(v) => setDraft({ ...draft, route: v })}
                  >
                    <SelectTrigger data-testid="drug-route" className="border-[#E2E8F0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROUTE_OPTIONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <MicroLabel>Frequency</MicroLabel>
                  <Select
                    value={draft.frequency}
                    onValueChange={(v) => setDraft({ ...draft, frequency: v })}
                  >
                    <SelectTrigger data-testid="drug-freq" className="border-[#E2E8F0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQ_OPTIONS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <MicroLabel>Duration</MicroLabel>
                  <Input
                    data-testid="drug-duration"
                    value={draft.duration}
                    onChange={(e) => setDraft({ ...draft, duration: e.target.value })}
                    placeholder="7 days"
                    className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                  />
                </div>
              </div>

              <div>
                <MicroLabel>Notes</MicroLabel>
                <Input
                  data-testid="drug-notes"
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  placeholder="Give with food / avoid in CKD / cat max 5 mg/kg…"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  data-testid="save-drug"
                  onClick={handleSave}
                  className="flex-1 rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white"
                >
                  <Save className="w-4 h-4 mr-1.5" /> {editingKey ? "Update drug" : "Add drug"}
                </Button>
                {editingKey && (
                  <Button
                    data-testid="cancel-edit"
                    variant="outline"
                    onClick={clearDraft}
                    className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Library table */}
        <section className="lg:col-span-8 order-1 lg:order-2">
          <div className="flex items-center justify-between gap-3 mb-4">
            <Input
              data-testid="drug-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search drug, route, frequency, notes…"
              className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
            />
            <Button
              data-testid="export-csv"
              variant="outline"
              onClick={() => handleExport("csv")}
              className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A] whitespace-nowrap"
            >
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
          </div>

          <Card className="border-[#E2E8F0] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drug-table">
                <thead className="bg-[#F9F8F6] text-[10px] uppercase tracking-[0.12em] text-[#5C6B66]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold w-1/4">Drug</th>
                    <th className="text-left px-3 py-3 font-semibold">mg/kg</th>
                    <th className="text-left px-3 py-3 font-semibold">Dose</th>
                    <th className="text-left px-3 py-3 font-semibold">Route</th>
                    <th className="text-left px-3 py-3 font-semibold">Freq.</th>
                    <th className="text-left px-3 py-3 font-semibold">Duration</th>
                    <th className="text-right px-4 py-3 font-semibold">Source / Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => {
                    const isCustom = d.__source === "custom";
                    return (
                      <tr
                        key={d.drug}
                        data-testid={`drug-row-${d.drug.replace(/\s+/g, "-").toLowerCase()}`}
                        className="border-t border-[#E2E8F0] align-top hover:bg-[#F9F8F6]/60"
                      >
                        <td className="px-4 py-3 font-medium">{d.drug}</td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {d.doseMgPerKg != null ? d.doseMgPerKg : <span className="text-[#5C6B66]">—</span>}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">{d.doseText || "—"}</td>
                        <td className="px-3 py-3 whitespace-nowrap">{d.route}</td>
                        <td className="px-3 py-3 whitespace-nowrap">{d.frequency}</td>
                        <td className="px-3 py-3 whitespace-nowrap">{d.duration || "—"}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {isCustom ? (
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                data-testid={`edit-${d.drug.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => startEdit(d)}
                                className="text-[11px] px-2 py-1 rounded-md border border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A] transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                data-testid={`delete-${d.drug.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => handleDelete(d.drug)}
                                aria-label="Delete"
                                className="h-7 w-7 grid place-items-center rounded-md border border-[#E2E8F0] hover:border-[#E53E3E]/60 hover:text-[#E53E3E] transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                              <Badge className="bg-[#0F4C3A] text-white text-[10px] font-normal ml-1">
                                Custom
                              </Badge>
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                data-testid={`clone-${d.drug.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => startEdit({ ...d })}
                                className="text-[11px] px-2 py-1 rounded-md border border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A] transition-colors"
                              >
                                Edit copy
                              </button>
                              <Badge variant="outline" className="border-[#E2E8F0] text-[10px] font-normal">
                                Built-in
                              </Badge>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-[#5C6B66]">
                        No drug matched. Try a different search or add a new one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Import format hint */}
          <div className="mt-5 rounded-md border border-[#0F4C3A]/20 bg-[#0F4C3A]/5 p-4 text-[12.5px] text-[#1A1F1D]">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="w-4 h-4 text-[#0F4C3A] mt-0.5 shrink-0" />
              <div>
                <div className="font-medium mb-1">Excel / CSV import format</div>
                <p className="text-[#5C6B66] leading-relaxed">
                  Column headers (row 1):{" "}
                  <code className="text-[11px] bg-white border border-[#E2E8F0] rounded px-1 py-0.5">drug</code>,{" "}
                  <code className="text-[11px] bg-white border border-[#E2E8F0] rounded px-1 py-0.5">dose_mg_per_kg</code>,{" "}
                  <code className="text-[11px] bg-white border border-[#E2E8F0] rounded px-1 py-0.5">dose_label</code>,{" "}
                  <code className="text-[11px] bg-white border border-[#E2E8F0] rounded px-1 py-0.5">route</code>,{" "}
                  <code className="text-[11px] bg-white border border-[#E2E8F0] rounded px-1 py-0.5">frequency</code>,{" "}
                  <code className="text-[11px] bg-white border border-[#E2E8F0] rounded px-1 py-0.5">duration</code>,{" "}
                  <code className="text-[11px] bg-white border border-[#E2E8F0] rounded px-1 py-0.5">notes</code>.<br />
                  Only <strong>drug</strong> is mandatory. Duplicate drug names update existing entries.
                  Download the template above for a ready-to-fill file.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function blankDraft() {
  return {
    drug: "",
    doseMgPerKg: null,
    doseText: "",
    route: "PO",
    frequency: "SID",
    duration: "",
    notes: "",
  };
}

function MicroLabel({ children }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold block mb-1">
      {children}
    </Label>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
